/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  CommunicationEntryPoint,
  CommunicationListItem,
  EmailDialogConfig,
  emailDialogRoute,
  isJobEditable,
  JobDetailsModel,
  MeetingDialogConfig,
  meetingDialogRoute,
  meetingToCommunicationItem,
  SmsDialogConfig,
  smsDialogRoute,
} from '@gh/builder-shared';
import {
  ActivityDetails,
  BuilderPermission,
  CommunicationType,
  ContactType,
  JobCommunicationRestService,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import {
  htmlTextCellRenderer,
  notEmptyCellRenderer,
  rendererIconByName,
  ROW_UNREAD_CLASS,
  ROW_WARNING_CLASS,
  textMessageDialogRoute,
} from '@gh/common-internal-shared';
import { DateTimeTypes, EnumDictionary, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService, translationTuple } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  GridDefinitionFactory,
  GridRowEvent,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { createLink, currentDate } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';

import { assign, filter, first } from 'lodash';
import {
  CommunicationData,
  getJobCommunicationListItemId,
  jobCommunicationListArr,
  JobDetailsTabBusinessService,
} from '../../../resources/job-details-tab';

@Component({
  standalone: false,
  selector: 'gh-job-details-communication-list-tab',
  templateUrl: 'job-details-communication-list-tab.component.html',
  styleUrls: ['job-details-communication-list-tab.scss'],
})
export class JobDetailsCommunicationListTabComponent implements OnInit {
  showSms: boolean = true;
  showEmail: boolean = true;
  showMeetings: boolean = true;
  showDocs: boolean = true;
  showWebsite: boolean = false;
  filteredCommunications: CommunicationListItem[];
  communicationData: CommunicationData;
  gridDefinition: LayoutBasedGridDefinition;
  communicationTypes: EnumDictionary<CommunicationType>;
  jobId: ID;
  contactId: ID;
  isUnsubscribed: boolean;
  isFinalized: boolean;
  constructor(public route: ActivatedRoute,
              private model: JobDetailsModel,
              private dialogService: DialogService,
              private translationService: TranslationService,
              private navigationService: BuilderNavigationService,
              private businessService: JobDetailsTabBusinessService,
              private waitingService: WaitingService,
              private userContext: BuilderUserContext,
              private communicationService: JobCommunicationRestService,
              private notificationService: NotificationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  get editMode(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
      && isJobEditable(this.model.basicInfo);
  }

  ngOnInit(): void {
    const { jobId, communicationTypes, gridLayout } = getRouteInitData(this.route);
    let { communicationData } = getRouteInitData(this.route);
    assign(this, { communicationData, jobId, communicationTypes });
    this.isUnsubscribed = communicationData.isUnsubscribed;
    const date = currentDate();
    this.model.basicInfo$.subscribe((basicInfo) => {
      this.isFinalized = basicInfo.isFinalized;
      this.communicationService.get(jobId).subscribe((result) => {
        communicationData = result;
        assign(this, { communicationData, jobId, communicationTypes });
        this.updateFilteredCommunications();
      });
    });
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        communicationType: {
          valueFormatter: (params: any) => params.data.communicationType
            ? this.communicationTypes.get(params.data.communicationType)
            : '',
        },
        created: {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          sort: 'desc',
        },
        createdByName: {
          valueFormatter: (params: any) => {
            if (params.data.createdByName) {
              return params.data.createdByName;
            }
            if (params.data.milestoneEvent &&
              (params.data.communicationType === CommunicationType.Email
                || params.data.communicationType === CommunicationType.Email)) {
              return this.translationService.get('lbl_milestone_event');
            }
            return '';
          },
        },
        message: {
          cellRenderer: htmlTextCellRenderer,
        },
        attachments: {
          cellRenderer: notEmptyCellRenderer(rendererIconByName('attach-file')),
          sortable: false,
        },
      },
      {
        getRowId: (params) => String(params.data.communicationId!),
        rowClassRules: {
          [ROW_WARNING_CLASS]: ({ data }: { data: CommunicationListItem }) =>
            !!(!data.isDone && (data.communicationType === CommunicationType.JobActivity
              || data.communicationType === CommunicationType.SalesActivity
              || data.communicationType === CommunicationType.ContactActivity) && data.startDate && data.startDate < date),
          [ROW_UNREAD_CLASS]: ({ data }: { data: CommunicationListItem }) =>
            data.communicationType === CommunicationType.IncomingSms && !data.isMarkedAsRead,
        },
      });
    this.gridDefinition.bind(gridLayout);
    this.updateFilteredCommunications();
  }

  updateFilteredCommunications(): void {
    const communications = this.communicationData.communications;

    if (this.showEmail && this.showSms && this.showMeetings && this.showDocs && this.showWebsite) {
      this.filteredCommunications = communications;
    } else if (!this.showEmail && !this.showSms && !this.showMeetings && !this.showDocs && !this.showWebsite) {
      this.filteredCommunications = [];
    } else {
      const filterOptions = {
        [CommunicationType.OutgoingSms]: this.showSms,
        [CommunicationType.IncomingSms]: this.showSms,
        [CommunicationType.Email]: this.showEmail,
        [CommunicationType.JobMeeting]: this.showMeetings,
        [CommunicationType.JobActivity]: this.showMeetings,
        [CommunicationType.SalesActivity]: this.showMeetings,
        [CommunicationType.ContactActivity]: this.showMeetings,
        [CommunicationType.SalesMeeting]: this.showMeetings,
        [CommunicationType.Doc]: this.showDocs,
        [CommunicationType.Website]: this.showWebsite,
      };
      this.filteredCommunications = filter(
        communications,
        (communication) => filterOptions[communication.communicationType]);
    }
  }

  addMeeting(): void {
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint: CommunicationEntryPoint.Job,
      contactId: this.model.basicInfo.contactId,
      jobId: this.jobId,
      mode: FormMode.CreateMode,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((meeting: any) => {
        this.communicationData.communications = jobCommunicationListArr.append(
          this.communicationData.communications,
          this.meetingToCommunicationItem(meeting));
        this.updateFilteredCommunications();
        if (meeting.isDone) {
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
            filter$(yesAnswer))
            .subscribe(() => this.addMeeting());
        }
      });
  }

  sendEmail(): void {
    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
      entryPoint: CommunicationEntryPoint.Job,
      contactId: this.model.basicInfo.contactId,
      jobId: this.jobId,
      gFolderId: this.model.basicInfo.jobDocsFolderId,
      email: {
        jobId: this.jobId,
        contactId: this.model.basicInfo.contactId,
        email: this.model.basicInfo.basicEmail,
        subject: '',
        message: '',
        communicationType: CommunicationType.Email,
        attachments: [],
        isMarkedAsRead: true,
      },
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((email: any) => {
        this.communicationData.communications =
          jobCommunicationListArr.append(this.communicationData.communications, email);
        this.updateFilteredCommunications();
      });
  }

  sendSms(): void {
    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
        entryPoint: CommunicationEntryPoint.Job,
        contactId: this.model.basicInfo.contactId,
        jobId: this.jobId,
        sms: {
          jobId: this.jobId,
          contactId: this.model.basicInfo.contactId,
          cellPhone: this.model.basicInfo.contactPhone,
          subject: '',
          message: '',
          communicationType: CommunicationType.Email,
          attachments: [],
          isMarkedAsRead: true,
        },
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((sms: any) => {
        this.communicationData.communications =
          jobCommunicationListArr.append(this.communicationData.communications, sms);
        this.updateFilteredCommunications();
      });
  }

  showEmailDialog(communicationListItem: CommunicationListItem): void {
    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
      entryPoint: CommunicationEntryPoint.Job,
      email: communicationListItem,
      contactId: this.model.basicInfo.contactId,
      jobId: this.jobId,
      communicationId: communicationListItem.communicationId,
    });
  }

  showSmsDialog(communicationListItem: CommunicationListItem): void {
    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
      entryPoint: CommunicationEntryPoint.Job,
      contactId: this.model.basicInfo.contactId,
      jobId: this.jobId,
      sms: communicationListItem,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((item: CommunicationListItem) => {
        this.communicationData.communications =
          jobCommunicationListArr.update(this.communicationData.communications, item.communicationId, item);
        this.updateFilteredCommunications();
      });
  }

  showMeetingDialog(communicationListItem: CommunicationListItem, formMode: FormMode | boolean, entryPoint: CommunicationEntryPoint): void {
    if (!formMode) {
      this.dialogService.error('msg_denied_no_permissions');
      return;
    }
    const isDone = communicationListItem.isDone;
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint,
      contactId: this.model.basicInfo.contactId,
      jobId: entryPoint === CommunicationEntryPoint.Job ? communicationListItem.jobId : undefined,
      mode: formMode,
      salesFolderId: entryPoint === CommunicationEntryPoint.LeadCommunication ? communicationListItem.salesFolderId : undefined,
      leadId: entryPoint === CommunicationEntryPoint.LeadCommunication ? communicationListItem.contactId : undefined,
      meetingId: communicationListItem.communicationId,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((meeting: any) => {
        this.communicationData.communications = jobCommunicationListArr.update(
          this.communicationData.communications,
          getJobCommunicationListItemId(communicationListItem),
          meeting);
        this.updateFilteredCommunications();
        if (meeting.isDone && !isDone) {
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
            filter$(yesAnswer))
            .subscribe(() => this.addMeeting());
        }
      });
  }

  showDocDialog(communicationListItem: CommunicationListItem): void {
    this.businessService
      .getCommunicationDetails(communicationListItem.communicationId!).pipe( 
      this.waitingService.operator())
      .subscribe((fileInfo: CommunicationListItem) => {
        const attachment = first(fileInfo.attachments);
        const link = createLink(attachment!.webViewLink, attachment!.googleFileName);

        this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
          title: this.translationService.get('lbl_generated_doc'),
          html: `${this.translationService.get('lbl_link_to_generated_doc')}:\n${link}`,
          buttons: [
            { title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' },
          ],
        });
      },         this.notificationService.processAll);
  }

  onShowSmsChanged(value: boolean): void {
    this.showSms = value;
    this.updateFilteredCommunications();
  }

  onShowEmailChanged(value: boolean): void {
    this.showEmail = value;
    this.updateFilteredCommunications();
  }

  onShowWebsiteChanged(value: boolean): void {
    this.showWebsite = value;
    this.updateFilteredCommunications();
  }

  onShowMeetingsChanged(value: boolean): void {
    this.showMeetings = value;
    this.updateFilteredCommunications();
  }

  onShowDocsChanged(value: boolean): void {
    this.showDocs = value;
    this.updateFilteredCommunications();
  }

  onRowClicked(event: GridRowEvent<CommunicationListItem>): void {
    const item = event.data;

    switch (item.communicationType) {
      case CommunicationType.Email:
        this.showEmailDialog(item);
        break;
      case CommunicationType.OutgoingSms:
      case CommunicationType.IncomingSms:
        this.showSmsDialog(item);
        break;
      case CommunicationType.Doc:
        this.showDocDialog(item);
        break;
      case CommunicationType.JobMeeting:
        this.showMeetingDialog(item, this.formModeForJobActivity(), CommunicationEntryPoint.Job);
        break;
      case CommunicationType.JobActivity:
        this.showMeetingDialog(item, this.formModeForJobActivity(), CommunicationEntryPoint.Job);
        break;
      case CommunicationType.SalesMeeting:
        this.showMeetingDialog(item, this.formModeForSalesActivity(), CommunicationEntryPoint.LeadCommunication);
        break;
      case CommunicationType.SalesActivity:
        this.showMeetingDialog(item, this.formModeForSalesActivity(),  CommunicationEntryPoint.LeadCommunication);
        break;
      case CommunicationType.ContactActivity:
        this.showMeetingDialog(item, this.formModeForContactActivity(item), CommunicationEntryPoint.Contact);
        break;
      default:
        break;
    }
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  private meetingToCommunicationItem(meeting: ActivityDetails, createdByName?: string): CommunicationListItem {
    return meetingToCommunicationItem(
      {
        ...meeting,
        contactId: this.model.basicInfo.contactId,
        jobId: this.jobId,
        createdByName: createdByName ? createdByName : this.userContext.data.fullName,
      },
      {...meeting, addedDate: meeting.created});
  }

  private formModeForJobActivity(): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)) {
      return FormMode.EditMode;
    }
    if (this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }

  private formModeForSalesActivity(): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT)) {
      return FormMode.EditMode;
    }
    if (this.userContext.hasPermission(BuilderPermission.Sales.Leads.VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }

  private formModeForContactActivity(item: CommunicationListItem): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || (!!item.contactTypeCode
        && this.userContext.hasPermission(BuilderPermission.Contacts[ContactType[item.contactTypeCode]].CREATE_EDIT))) {
      return FormMode.EditMode;
    }
    if (!item.contactTypeCode) {
      return false;
    }
    if (this.userContext.hasPermission(BuilderPermission.Contacts[ContactType[item.contactTypeCode]].VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }
}
